/**
 * URL building and validation utilities
 */
import { REVIEW_URL_PATH } from './constants';
import { isIntroStep, isReviewStep, parseUrlStepId } from './stepValidation';

/**
 * Builds a navigation URL for a step/substep
 * Handles special cases:
 * - Intro step (0) → baseUrl only (no step/substep in path)
 * - Review step (999) → baseUrl/review (no substep in path)
 * - Regular steps → baseUrl/stepId or baseUrl/stepId/substepId
 * @param {string} baseUrl - Base URL path
 * @param {string|number} stepId - Step ID
 * @param {string|number} [substepId] - Optional substep ID
 * @returns {string} Full navigation URL
 */
export const buildNavigationUrl = (baseUrl, stepId, substepId = null) => {
  // Intro step: just the base URL with no extra paths
  if (isIntroStep(stepId)) {
    return baseUrl;
  }

  // Review step: baseUrl/review (no substep in path)
  if (isReviewStep(stepId)) {
    return `${baseUrl}/${REVIEW_URL_PATH}`;
  }

  // Regular steps: include step_id and optionally substep_id
  if (substepId !== null) {
    return `${baseUrl}/${stepId}/${substepId}`;
  }
  return `${baseUrl}/${stepId}`;
};

/**
 * Determines if URL needs to be updated based on current state
 * Handles special cases for intro (no step_id) and review ('review') steps
 * @param {Object} params - Current URL params
 * @param {string|number} stepId - Expected step ID
 * @param {string|number} [substepId] - Expected substep ID
 * @returns {boolean} True if URL needs update
 */
export const urlNeedsUpdate = (params, stepId, substepId = null) => {
  const { step_id: urlStepId, substep_id: urlSubstepId } = params;

  // Parse the URL step_id to handle special cases
  const parsedUrlStepId = parseUrlStepId(urlStepId);

  // For intro step, URL should have no step_id
  if (isIntroStep(stepId)) {
    return urlStepId !== undefined;
  }

  // For review step, URL should have 'review' as step_id
  if (isReviewStep(stepId)) {
    return urlStepId !== REVIEW_URL_PATH;
  }

  // For regular steps, compare normally
  if (substepId !== null) {
    return String(parsedUrlStepId) !== String(stepId) || String(urlSubstepId) !== String(substepId);
  }
  return String(parsedUrlStepId) !== String(stepId) || urlSubstepId;
};
